// Language Context for PRIVIA YACHTS
import React, { createContext, useContext, useState, useEffect } from 'react';
import translations from '../translations';

// French translations (current content)
const frenchTranslations = {
  // Navigation
  nav: {
    home: 'Accueil',
    collection: 'Collection',
    destinations: 'Destinations',
    services: 'Services',
    about: 'À Propos',
    contact: 'Contact'
  },
  
  // Hero section
  hero: {
    title: 'Voyages Privés Perfectionnés',
    subtitle: 'Découvrez les yachts les plus exclusifs au monde',
    description: 'Vivez le luxe redéfini avec notre collection de superyachts, où chaque détail est conçu pour le voyageur exigeant.',
    cta: 'Explorer la Collection',
    stats: {
      yachts: 'Yachts Exclusifs',
      destinations: 'Destinations',
      experience: 'Années d\'Expérience'
    }
  },
  
  // Collection page
  collection: {
    title: 'Collection de Yachts',
    subtitle: 'Découvrez Notre Flotte',
    loading: 'Chargement...',
    perDay: 'par jour',
    guests: 'invités',
    crew: 'équipage',
    viewDetails: 'Voir les Détails',
    bookNow: 'Réserver Maintenant'
  },
  
  // Yacht detail page
  detail: {
    // Quick info
    length: 'Longueur',
    guests: 'Invités',
    crew: 'Équipage',
    perDay: 'Par Jour',
    
    // Sections
    artOfTravel: 'L\'Art du Voyage',
    exceptionalExperience: 'Une Expérience Exceptionnelle',
    accommodation: 'Hébergement',
    presentation: 'Présentation',
    configuration: 'Configuration',
    suites: 'suites',
    cabins: 'cabines',
    maximum: 'Maximum',
    
    // Equipment
    exceptionalEquipment: 'Équipement Exceptionnel',
    luxuryRefinement: 'Luxe & Raffinement',
    
    // Specifications
    technicalSpecs: 'Spécifications Techniques',
    performanceDesign: 'Performance & Design',
    beam: 'Largeur',
    draft: 'Tirant d\'eau',
    maxSpeed: 'Vitesse Max',
    cruisingSpeed: 'Vitesse de Croisière',
    range: 'Autonomie',
    builder: 'Constructeur',
    year: 'Année',
    fuel: 'Carburant',
    engines: 'Moteurs',
    materials: 'Matériaux',
    hull: 'Coque',
    superstructure: 'Superstructure',
    design: 'Design',
    exterior: 'Extérieur',
    interior: 'Intérieur',
    tonnage: 'Tonnage',
    
    // Pricing & Destinations
    pricingDestinations: 'Tarifs & Destinations',
    seasonsRegions: 'Saisons & Régions de Croisière',
    summer: 'ÉTÉ',
    winter: 'HIVER',
    highSeason: 'Haute saison',
    lowSeason: 'Basse saison',
    customPricing: 'Tarification Personnalisée',
    contactAdvisors: 'Contactez nos conseillers pour un devis personnalisé selon vos dates et destinations.',
    availableDestinations: 'Destinations Disponibles',
    mediterranean: 'Méditerranée',
    caribbean: 'Caraïbes',
    otherRegions: 'Autres Régions',
    
    // Photo Gallery
    photoGallery: 'Galerie Photos',
    discoverDetails: 'Découvrez Chaque Détail',
    mainImage: 'Image Principale',
    galleryComingSoon: 'Galerie Bientôt Disponible',
    photosAvailableSoon: 'Les photos de ce yacht seront bientôt disponibles. Contactez-nous pour plus d\'informations.',
    viewAllPhotos: 'Voir Toutes les Photos',
    
    // CTA
    readyForEscape: 'Prêt pour l\'Évasion ?',
    createUniqueExperience: 'Créez Votre Expérience Unique',
    advisorsAccompany: 'Nos conseillers spécialisés vous accompagnent dans la création d\'un voyage sur-mesure, adapté à vos désirs les plus exigeants.',
    bookThisYacht: 'Réserver ce Yacht',
    discoverOtherYachts: 'Découvrir d\'Autres Yachts'
  },
  
  // Search & Filters
  search: {
    searchYachts: 'Rechercher des Yachts',
    allDestinations: 'Toutes les Destinations',
    allTypes: 'Tous les Types',
    motorYacht: 'Yacht à Moteur',
    sailingYacht: 'Yacht à Voile',
    catamaran: 'Catamaran',
    explorer: 'Explorateur',
    classic: 'Classique'
  },
  
  // Footer
  footer: {
    tagline: 'Voyages Privés Perfectionnés',
    description: 'Vivez le luxe redéfini avec notre collection de superyachts.',
    quickLinks: 'Liens Rapides',
    destinations: 'Destinations',
    contact: 'Contact',
    followUs: 'Suivez-nous',
    allRightsReserved: 'Tous droits réservés.'
  },
  
  // Common
  common: {
    loading: 'Chargement...',
    error: 'Erreur',
    tryAgain: 'Réessayer',
    close: 'Fermer',
    next: 'Suivant',
    previous: 'Précédent',
    more: 'Plus',
    less: 'Moins'
  }
};

// Available languages
const languages = {
  en: {
    name: 'English',
    flag: '🇺🇸',
    translations: translations
  },
  fr: {
    name: 'Français',
    flag: '🇫🇷',
    translations: frenchTranslations
  }
};

// Create Language Context
const LanguageContext = createContext();

// Language Provider Component
export const LanguageProvider = ({ children }) => {
  // Get saved language from localStorage or default to English
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('privia-language');
    return saved || 'en'; // Default to English
  });

  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem('privia-language', currentLanguage);
  }, [currentLanguage]);

  // Get current translations
  const t = languages[currentLanguage]?.translations || translations;

  // Switch language function
  const switchLanguage = (langCode) => {
    if (languages[langCode]) {
      setCurrentLanguage(langCode);
    }
  };

  // Get available languages
  const getAvailableLanguages = () => {
    return Object.entries(languages).map(([code, lang]) => ({
      code,
      name: lang.name,
      flag: lang.flag
    }));
  };

  const value = {
    currentLanguage,
    t,
    switchLanguage,
    getAvailableLanguages,
    languages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
